import React, { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, makeStyles } from "@material-ui/core";

import { WebTrackerReportTrackerStats } from "../types";
import TrackerNotesContent from "./TrackerNotes/TrackerNotesContent";
import useFetchWebTrackerResolution from "../queries/useFetchWebTrackerResolution";
import useAddWebTrackerNotes from "../mutations/useAddWebTrackerNotes";

function isNoteEmpty(note: string): boolean {
  note = note.trim();
  return note === "" || note === "<p><br></p>";
}

const useStyles = makeStyles((theme) => ({
  notesHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
    padding: theme.spacing(2),
  },
  notesContent: {
    padding: theme.spacing(2, 2, 1, 2),
    "&:last-child": {
      paddingBottom: theme.spacing(2),
    },
  },
}));

export type NotesEditorProps = {
  webTrackerReportStats: WebTrackerReportTrackerStats;
};

export enum TrackerNotesState {
  LOADING,
  EMPTY,
  EDITING,
  VIEWING,
}

function TrackerNotes({ webTrackerReportStats }: NotesEditorProps) {
  const classes = useStyles();

  const trackerIdentifier = webTrackerReportStats.foundDomains[0];

  const {
    isLoading: webTrackerResolutionIsLoading,
    data: { webTrackerResolution } = {},
  } = useFetchWebTrackerResolution(trackerIdentifier);

  const addWebTrackerNotesMutation = useAddWebTrackerNotes();

  const note = webTrackerResolution?.note ?? "";

  const [content, setContent] = useState(note);
  const [isOpen, setIsOpen] = useState(false);

  const handleSaveNotes = () => {
    setIsOpen(false);
    addWebTrackerNotesMutation.mutate({
      trackerDomain: trackerIdentifier,
      trackerName: webTrackerReportStats.tracker,
      note: content,
    });
  };

  const handleEditNotes = () => {
    setContent(note);
    setIsOpen(true);
  };

  const handleCloseNotes = () => {
    setIsOpen(false);
  };

  const trackerNotesState = useMemo(() => {
    if (webTrackerResolutionIsLoading) {
      return TrackerNotesState.LOADING;
    }

    if (isNoteEmpty(note) && !isOpen) {
      return TrackerNotesState.EMPTY;
    }

    if (isOpen) {
      return TrackerNotesState.EDITING;
    }

    return TrackerNotesState.VIEWING;
  }, [isOpen, note, webTrackerResolutionIsLoading]);

  return (
    <Card>
      <CardHeader className={classes.notesHeader} title="Notes" />
      <CardContent className={classes.notesContent}>
        <TrackerNotesContent
          content={note}
          onContentChanged={setContent}
          onClickEditNotes={handleEditNotes}
          onClickCloseNotes={handleCloseNotes}
          onClickSaveNotes={handleSaveNotes}
          trackerNotesState={trackerNotesState}
        />
      </CardContent>
    </Card>
  );
}

export default TrackerNotes;
